import React, { useState } from "react";
import styled from "styled-components";
import {
  Bell,
  FolderKanban,
  FileText,
  Award,
  MessageSquare,
  Info,
} from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { EmptyState, Badge } from "components/dashboard";

const C_PRIMARY = "#ff6c00";

// ─── Styled ────────────────────────────────────────────────────────────────────

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #e5e7eb;
  border: 1px solid #e5e7eb;
`;

const Item = styled.div<{ $unread?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.1rem 1.5rem;
  background: ${(p) => (p.$unread ? "#fffbf7" : "white")};
  cursor: pointer;
  transition: background 0.12s;
  border-left: 3px solid ${(p) => (p.$unread ? C_PRIMARY : "transparent")};
  &:hover {
    background: #fafafa;
  }
`;

const IconBox = styled.div<{ $color: string }>`
  width: 38px;
  height: 38px;
  background: ${(p) => p.$color}18;
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;
const Title = styled.p<{ $unread?: boolean }>`
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$unread ? 800 : 600)};
  color: #111;
  margin-bottom: 2px;
`;
const Desc = styled.p`
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.4;
`;
const Time = styled.span`
  font-size: 0.65rem;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button<{ $active?: boolean }>`
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.4rem 1rem;
  border: 1.5px solid ${(p) => (p.$active ? C_PRIMARY : "#e5e7eb")};
  background: ${(p) => (p.$active ? "#fff3e8" : "white")};
  color: ${(p) => (p.$active ? C_PRIMARY : "#6b7280")};
  cursor: pointer;
  transition: all 0.12s;
  &:hover {
    border-color: ${C_PRIMARY};
    color: ${C_PRIMARY};
  }
`;

// ─── Mock data ──────────────────────────────────────────────────────────────────

type NotifCategory = "all" | "dossier" | "document" | "attestation" | "message";

const MOCK_NOTIFS = [
  {
    id: 1,
    title: "Dossier mis à jour",
    desc: "L'étape « Visa étudiant » de votre dossier Canada a été validée.",
    time: "Il y a 2 h",
    unread: true,
    category: "dossier" as const,
    icon: FolderKanban,
    color: "#3b82f6",
  },
  {
    id: 2,
    title: "Document rejeté",
    desc: "Votre relevé de notes a été rejeté. Motif : document illisible.",
    time: "Il y a 5 h",
    unread: true,
    category: "document" as const,
    icon: FileText,
    color: "#ef4444",
  },
  {
    id: 3,
    title: "Attestation émise",
    desc: "Une nouvelle attestation de virement AVI a été générée pour vous.",
    time: "Hier",
    unread: false,
    category: "attestation" as const,
    icon: Award,
    color: "#22c55e",
  },
  {
    id: 4,
    title: "Nouveau message",
    desc: "Votre conseiller MomsEduc vous a envoyé un message.",
    time: "Hier",
    unread: false,
    category: "message" as const,
    icon: MessageSquare,
    color: C_PRIMARY,
  },
  {
    id: 5,
    title: "Information importante",
    desc: "Pensez à compléter votre dossier avant le 30 mai 2026.",
    time: "Il y a 3 j",
    unread: false,
    category: "dossier" as const,
    icon: Info,
    color: "#f97316",
  },
];

const FILTERS: { key: NotifCategory; label: string }[] = [
  { key: "all", label: "Toutes" },
  { key: "dossier", label: "Dossiers" },
  { key: "document", label: "Documents" },
  { key: "attestation", label: "Attestations" },
  { key: "message", label: "Messages" },
];

// ─── Component ─────────────────────────────────────────────────────────────────

const NotificationsPage: React.FC = () => {
  const [filter, setFilter] = useState<NotifCategory>("all");
  const [read, setRead] = useState<Set<number>>(new Set());

  const visible = MOCK_NOTIFS.filter(
    (n) => filter === "all" || n.category === filter,
  );
  const unreadCount = MOCK_NOTIFS.filter(
    (n) => n.unread && !read.has(n.id),
  ).length;

  return (
    <DashboardLayout title="Notifications">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1.25rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <p style={{ fontWeight: 800, fontSize: "0.9rem", color: "#111" }}>
            Notifications
          </p>
          {unreadCount > 0 && (
            <Badge variant="primary">
              {unreadCount} non lue{unreadCount > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => setRead(new Set(MOCK_NOTIFS.map((n) => n.id)))}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: C_PRIMARY,
              fontSize: "0.78rem",
              fontWeight: 700,
            }}
          >
            Tout marquer comme lu
          </button>
        )}
      </div>

      {/* Filtres */}
      <FilterRow>
        {FILTERS.map((f) => (
          <FilterBtn
            key={f.key}
            $active={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </FilterBtn>
        ))}
      </FilterRow>

      {/* Liste */}
      {visible.length === 0 ? (
        <EmptyState
          icon={<Bell size={40} />}
          title="Aucune notification"
          description="Vous n'avez pas encore de notification dans cette catégorie."
        />
      ) : (
        <List>
          {visible.map((n) => {
            const isUnread = n.unread && !read.has(n.id);
            return (
              <Item
                key={n.id}
                $unread={isUnread}
                onClick={() =>
                  setRead((prev) => {
                    const s = new Set(prev);
                    s.add(n.id);
                    return s;
                  })
                }
              >
                <IconBox $color={n.color}>
                  <n.icon size={16} />
                </IconBox>
                <Body>
                  <Title $unread={isUnread}>{n.title}</Title>
                  <Desc>{n.desc}</Desc>
                </Body>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 6,
                  }}
                >
                  <Time>{n.time}</Time>
                  {isUnread && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        background: C_PRIMARY,
                        display: "inline-block",
                      }}
                    />
                  )}
                </div>
              </Item>
            );
          })}
        </List>
      )}
    </DashboardLayout>
  );
};

export default NotificationsPage;
